// What the super-admin onboarding view reads out of an intake submission: the
// readable label for each answer, and the "Before the session" flags.
//
// Kept out of the page so the flags can be pinned by a test
// (tests/client/onboarding-flags.test.ts). The flags are the point of the
// fit check: Ralph Pitt (2026-09) turned out to be on QuickBooks Desktop, with
// delivery days by route, prices by customer type and case/each units — all
// discovered mid-onboarding. Each answer that means set-up or custom work has to
// surface here, before the session is booked.

export type IntakeAnswers = Record<string, any>;
export type IntakeFile = { kind: string };

export const ANSWER_LABELS: Record<string, string> = {
  staffCount: "Staff logins",
  staff: "Staff",
  qbProduct: "Accounting software",
  qbProductOther: "Accounting software (named)",
  qbIntegrate: "Connect QuickBooks Online",
  qbInvoiceNumbering: "Invoice numbering",
  qbManualEntry: "Team hand-keys invoices",
  qbProductsServices: "Uses Products & Services list",
  clientListText: "Client list (typed)",
  multiAccountContacts: "One contact, multiple accounts",
  multiContactAccounts: "One account, multiple contacts",
  productListText: "Product list (pasted)",
  hasPhotos: "Has product photos",
  showPrices: "Show prices to customers",
  pricingVaries: "Prices by customer",
  sellingUnits: "Sells by",
  fulfillmentType: "Fulfillment",
  deliveryDaysVary: "Delivery days by customer",
  deliveryFrequency: "Delivery frequency",
  deliveryDay: "Delivery day",
  deliveryDays: "Delivery days",
  deliveryCutoffWhen: "Delivery cutoff",
  deliveryCutoffHour: "Delivery cutoff hour",
  deliveryCutoffMinute: "Delivery cutoff minute",
  pickupSameAsDelivery: "Pickup same as delivery",
  pickupFrequency: "Pickup frequency",
  pickupDay: "Pickup day",
  pickupDays: "Pickup days",
  pickupCutoffWhen: "Pickup cutoff",
  fulfillmentVariesByClient: "Fulfillment varies by client",
  paperworkNotes: "Paperwork that must look a certain way",
  oneChange: "One thing they'd change",
  anythingElse: "Anything else",
};

/** Readable values for the multiple-choice answers, keyed by answer. */
export const VALUE_LABELS: Record<string, Record<string, string>> = {
  qbProduct: {
    online: "QuickBooks Online",
    desktop: "QuickBooks Desktop",
    sage: "Sage",
    other: "A different accounting tool",
    none: "No accounting software",
  },
  deliveryDaysVary: {
    same: "Same days for everyone",
    by_area: "Depends on town or route",
    by_customer: "Depends on the customer",
  },
  pricingVaries: {
    same: "Everyone pays the same",
    by_type: "Different prices by type of customer",
    by_customer: "Some customers have their own prices",
  },
  sellingUnits: {
    case: "By the case",
    each: "By the each",
    weight: "By weight",
    mix: "A mix",
  },
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function renderAnswer(key: string, v: any): string {
  if (v == null || v === "") return "—";
  const labels = VALUE_LABELS[key];
  if (labels && typeof v === "string") return labels[v] ?? v;
  if (key === "deliveryDay" || key === "pickupDay") return DAY_NAMES[v] ?? String(v);
  if (key === "deliveryDays" || key === "pickupDays") {
    return Array.isArray(v) ? v.map((d: number) => DAY_NAMES[d]).join(", ") : String(v);
  }
  if (key === "staff" && Array.isArray(v)) {
    return v.filter((s) => s?.name || s?.email).map((s) => `${s.name || "(no name)"} <${s.email || "?"}>`).join(", ") || "—";
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const hasText = (v: unknown) => typeof v === "string" && v.trim() !== "";

/** Accounting tools that cannot connect — invoicing stays manual for these. */
const NON_QBO_TOOLS = new Set(["desktop", "sage", "other"]);

function accountingToolName(a: IntakeAnswers): string {
  if (a.qbProduct === "desktop") return "QuickBooks Desktop";
  if (a.qbProduct === "sage") return "Sage";
  return hasText(a.qbProductOther) ? `another tool (${String(a.qbProductOther).trim()})` : "another accounting tool";
}

/** Answers that should stop the process — surfaced above the rest. */
export function flagsFor(a: IntakeAnswers, files: IntakeFile[] = []): string[] {
  const out: string[] = [];
  if (NON_QBO_TOOLS.has(a.qbProduct)) {
    out.push(
      `On ${accountingToolName(a)} — only QuickBooks Online integrates. Settle invoicing (migrate to QBO, or manual) BEFORE booking the session (Ralph Pitt, 2026-09).`,
    );
  }
  if (a.qbIntegrate === "yes" && a.qbInvoiceNumbering === "manual") {
    out.push("Manual invoice numbers — needs explicit numbering, not the default (Gomes failure mode).");
  }
  if (a.qbIntegrate === "yes" && a.qbManualEntry === "yes") {
    out.push("Team hand-keys invoices into QBO — DocNumber poisoning risk; check their numbering range.");
  }
  if (a.qbIntegrate === "yes" && a.qbInvoiceNumbering === "auto") {
    out.push("Check 'Custom transaction numbers' in their QBO — if ON, invoices sync with blank numbers.");
  }
  if (a.multiAccountContacts === "yes" && a.multiContactAccounts === "yes") {
    out.push("Both account groups AND multiple contacts — these can't apply to the same account.");
  }
  if (!a.productListText && a.qbIntegrate !== "yes") {
    out.push("No product list yet — start the follow-up; this is what usually stalls onboarding.");
  }
  if (a.deliveryDaysVary === "by_area") {
    out.push("Set up delivery zones — delivery days depend on town or route (and switch on days by zone).");
  }
  if (a.deliveryDaysVary === "by_customer") {
    out.push("Delivery days vary customer by customer — per-account days aren't built. See whether delivery zones cover it; otherwise scope it as custom work.");
  }
  if (a.pricingVaries === "by_type") {
    out.push("Set up customer types + price lists — prices differ by type of customer.");
  }
  if (a.pricingVaries === "by_customer") {
    out.push("Set up customer types + price lists — they said some customers have their own prices; check whether types cover it or it's custom work.");
  }
  if (hasText(a.paperworkNotes) || files.some((f) => f.kind === "paperwork")) {
    out.push("Review paperwork example — pick sheet / driver sheet / invoice layout they need.");
  }
  if (hasText(a.oneChange)) {
    out.push("Possible custom request — scope before the session (see \"One thing they'd change\").");
  }
  return out;
}
